import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { fireTradeToast } from "../../lib/tradeToast";
import { formatExecutionError } from "../../lib/executionError";
import { motion, AnimatePresence } from "framer-motion";
import { buildApiUrl } from "../../lib/api";
import {
    allowsManualEarningsDate,
    fetchEarningsOpportunity,
    getEarningsOpportunityNotice,
} from "../../lib/earningsOpportunity";
import {
    isSmallAccountMode,
    isStrategyAllowedForBuyingPower,
    SMALL_ACCOUNT_REJECTION_MESSAGE,
    SMALL_ACCOUNT_TOOLTIP,
} from "../../lib/smallAccountMode";
import SimulatePanel from "../SimulatePanel/SimulatePanel";
import "./WheelConfigPanel.css";

const STRATEGY_TO_SIM_MODE = {
    wheel:          "wheel",
    tactical_spread: "tactical_spreads",
    regime_spread:  "regime_spreads",
    earnings:       "earnings",
};

function buildSimLaunch(strategy, symbol, portfolioValue, buyingPower, selectedPreset) {
    const capital = Number(buyingPower ?? portfolioValue ?? 2500);
    const preset  = selectedPreset || (
        capital < 2500  ? "small_account"
      : capital < 10000 ? "conservative"
      : capital < 25000 ? "balanced"
      : "aggressive"
    );
    const mode = STRATEGY_TO_SIM_MODE[strategy] || "wheel";
    return {
        symbol,
        mode,
        initial_capital: capital,
        portfolio_value: capital,
        buying_power:    capital,
        days:            mode === "regime_spreads" ? 180 : 90,
        preset,
        token:           Date.now(),
    };
}

// ------------------------------------------------------------------ //
// Animation variants                                                   //
// ------------------------------------------------------------------ //

// Panel container — orchestrates child stagger
const panelVariants = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.045, delayChildren: 0.04 } },
};

// Each top-level section fades + slides up
const sectionVariants = {
    hidden:  { opacity: 0, y: 8  },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
};

const cardVariants = {
    hidden:  { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0,  transition: { duration: 0.25, ease: "easeOut" } },
    exit:    { opacity: 0, y: -6, transition: { duration: 0.15, ease: "easeIn"  } },
    hover:   { scale: 1.015,      transition: { duration: 0.2,  ease: "easeOut" } },
    tap:     { scale: 0.985,      transition: { duration: 0.1,  ease: "easeOut" } },
};

// ------------------------------------------------------------------ //
// Skeleton                                                             //
// ------------------------------------------------------------------ //

function PreviewSkeleton() {
    return (
        <div className="wcp-skeleton-body">
            <div className="wcp-skeleton" style={{ width: "55%", height: 13, borderRadius: 4 }} />
            <div className="wcp-skeleton" style={{ width: "40%", height: 28, borderRadius: 5 }} />
            <div className="wcp-skeleton" style={{ width: "70%", height: 11, borderRadius: 4 }} />
            <div className="wcp-skeleton" style={{ width: "90%", height: 10, borderRadius: 4 }} />
            <div className="wcp-skeleton" style={{ width: "75%", height: 10, borderRadius: 4 }} />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div className="wcp-skeleton" style={{ width: 88, height: 30, borderRadius: 6 }} />
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ //
// Config                                                               //
// ------------------------------------------------------------------ //

const PRESETS = {
    conservative: {
        label: "Conservative",
        description: "Low delta, safer strikes, 40% profit target",
        summary: "Lower risk · less premium · 21–45 day trades",
        take_profit: 0.4,
        roll_threshold: 2.0,
        put_delta_min: -0.25,
        put_delta_max: -0.15,
        call_delta_min: 0.15,
        call_delta_max: 0.25,
        min_dte: 21,
        max_dte: 45,
    },
    balanced: {
        label: "Balanced",
        description: "Standard delta range, 50% profit target",
        summary: "Medium risk · moderate premium · 7–30 day trades",
        take_profit: 0.5,
        roll_threshold: 2.0,
        put_delta_min: -0.35,
        put_delta_max: -0.25,
        call_delta_min: 0.25,
        call_delta_max: 0.35,
        min_dte: 7,
        max_dte: 30,
    },
    aggressive: {
        label: "Aggressive",
        description: "High delta, more premium, 65% profit target",
        summary: "Higher risk · more premium · 3–14 day trades",
        take_profit: 0.65,
        roll_threshold: 3.0,
        put_delta_min: -0.50,
        put_delta_max: -0.35,
        call_delta_min: 0.35,
        call_delta_max: 0.50,
        min_dte: 3,
        max_dte: 14,
    },
};

const ACTION_LABELS = {
    SELL_PUT:    "Sell Cash-Secured Put",
    SELL_CALL:   "Sell Covered Call",
    OPEN_DEBIT_SPREAD: "Open Debit Spread",
    TAKE_PROFIT: "Take Profit",
    ROLL:        "Roll Position",
    HOLD:        "Hold",
};
const EXECUTION_TIMEOUT_MS = 30000;

const STRATEGY_OPTIONS = [
    { id: "wheel", label: "Wheel Strategy" },
    { id: "tactical_spread", label: "Tactical Spread Strategy" },
    { id: "regime_spread", label: "Regime Spread Strategy" },
    { id: "earnings", label: "Earnings Strategy" },
];

function formatStrategyLabel(strategy) {
    return STRATEGY_OPTIONS.find((option) => option.id === strategy)?.label || "Strategy";
}

// ------------------------------------------------------------------ //
// Helpers                                                              //
// ------------------------------------------------------------------ //

function parseApiError(raw) {
    if (!raw) return "Preview failed.";
    if (typeof raw === "object") {
        raw = raw.message || raw.error || raw.tooltip || JSON.stringify(raw);
    }
    if (typeof raw === "string" && raw.trim().startsWith("{")) {
        try {
            const p = JSON.parse(raw);
            raw = p.message || p.error || p.details || raw;
        } catch (_) {}
    }
    const lower = String(raw).toLowerCase();
    if (lower.includes("too many requests") || lower.includes("rate limit"))
        return "Market data rate limit reached. Wait a moment and try again.";
    if (lower.includes("no options contracts") || lower.includes("market data unavailable"))
        return "No options data available for this symbol.";
    return String(raw);
}

function fmtDate(dateStr) {
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.split("-").map(Number);
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${months[m - 1]} ${d}`;
}

function daysLabel(days) {
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    return `${days}d away`;
}

function buildSyntheticRecord(symbol, dateStr) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const earningsDate = new Date(`${dateStr}T00:00:00`);
    const daysTo = Math.max(0, Math.round((earningsDate - today) / 86_400_000));
    return {
        found: true,
        symbol,
        synthetic: true,
        earnings: { earnings_date: dateStr, days_to_earnings: daysTo, iv_rank: null, price: 0 },
        opportunities: [],
        best_opportunity: null,
    };
}

function loadManualDates() {
    try {
        return JSON.parse(localStorage.getItem("earningsManualDates") || "{}");
    } catch {
        return {};
    }
}

function saveManualDate(symbol, dateStr) {
    const all = loadManualDates();
    all[symbol] = dateStr;
    localStorage.setItem("earningsManualDates", JSON.stringify(all));
}

function formatExpiration(dateStr, dte) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return dte != null ? `${label} · ${dte}d` : label;
}

function formatContractSummary(meta) {
    if (!meta) return null;
    if (meta.opportunity_strategy) {
        const direction = meta.opportunity_strategy === "call_debit_spread" ? "Bullish" : "Bearish";
        const strikes = meta.long_strike != null && meta.short_strike != null
            ? `$${Number(meta.long_strike).toFixed(0)} / $${Number(meta.short_strike).toFixed(0)}`
            : null;
        const exp = formatExpiration(meta.earnings_date || meta.expiration_date, meta.dte);
        return [direction, strikes, exp].filter(Boolean).join(" · ");
    }
    const strike = meta.strike_price != null ? `$${Number(meta.strike_price).toLocaleString()}` : null;
    const type   = meta.option_type === "put" ? "Put" : meta.option_type === "call" ? "Call" : null;
    const exp    = formatExpiration(meta.expiration_date, meta.dte);
    const delta  = meta.delta != null ? `Δ ${Number(meta.delta).toFixed(2)}` : null;
    return [strike, type, exp, delta].filter(Boolean).join(" · ");
}

function renderEarningsStatusMessage(data, symbol) {
    return getEarningsOpportunityNotice(data, symbol);
}

export function EarningsEmptyState({ earningsData, symbol, manualDate, onManualDateChange }) {
    const earningsStatusMessage = renderEarningsStatusMessage(earningsData, symbol);
    const showManualDateInput = allowsManualEarningsDate(earningsData);

    return (
        <div className="wcp-earnings-empty">
            <div className="wcp-earnings-empty-copy">
                {earningsStatusMessage}
            </div>
            {showManualDateInput ? (
                <div className="wcp-manual-date-section">
                    <label className="wcp-label">Expected earnings date</label>
                    <input
                        type="date"
                        className="wcp-date-input"
                        value={manualDate || ""}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={(event) => onManualDateChange?.(event.target.value)}
                    />
                </div>
            ) : null}
        </div>
    );
}

function buildExecutionToastMessage(execution, payload = {}) {
    const execStatus = String(execution?.status || "").toLowerCase();
    if (execStatus === "submitted") return { message: "Trade executed", type: "success" };
    if (execStatus === "queued") return { message: "Trade queued · Executes at market open", type: "info" };
    if (execStatus === "error") {
        return { message: execution?.error || payload?.error || "Execution failed", type: "error" };
    }
    if (execStatus === "dry_run") {
        if (payload?.lifecycle_preview_only) {
            return {
                message: payload?.lifecycle_preview_reason || "This strategy is currently preview-only, so no live order was placed.",
                type: "info",
            };
        }
        if (payload?.earnings_preview_only) {
            return {
                message: payload?.earnings_preview_reason || "Earnings strategy is currently preview-only, so no live order was placed.",
                type: "info",
            };
        }
        return { message: execution?.message || "Dry run complete — no order placed", type: "info" };
    }
    if (execStatus === "skipped") {
        return { message: execution?.message || "No order was placed.", type: "info" };
    }
    return {
        message: execution?.message || payload?.message || "Trade request completed.",
        type: "info",
    };
}

function buildTradeExplanation(preview, symbol) {
    if (!preview?.should_trade || !preview?.meta) return null;
    if (preview.meta.opportunity_strategy) {
        const isBullish = preview.meta.opportunity_strategy === "call_debit_spread";
        const width = Number(preview.meta.width || 0);
        const debit = Number(preview.meta.debit || preview.price || 0);
        return isBullish
            ? `This earnings setup buys upside exposure with a call debit spread. You pay about $${debit.toFixed(2)} to control a ${width.toFixed(0)}-point spread into the event.`
            : `This earnings setup buys downside exposure with a put debit spread. You pay about $${debit.toFixed(2)} to control a ${width.toFixed(0)}-point spread into the event.`;
    }
    const { option_type, strike_price } = preview.meta;
    const premium = preview.price;
    if (!premium || !strike_price) return null;
    if (option_type === "put")
        return `You're selling a cash-secured put to collect $${Number(premium).toFixed(2)} in premium. If ${symbol} drops below $${strike_price} before expiration, you may be assigned 100 shares.`;
    if (option_type === "call")
        return `You're selling a covered call to collect $${Number(premium).toFixed(2)} in premium. If ${symbol} rises above $${strike_price} before expiration, your shares may be called away.`;
    return null;
}

function buildWhyBullets(preview, strategy, maxCapital) {
    if (!preview?.should_trade || !preview?.meta) return [];
    const meta = preview.meta;

    if (strategy === "wheel") {
        const bullets = [];
        const { delta, dte, option_type } = meta;
        const premium = preview.price;

        if (delta != null) {
            const abs = Math.abs(delta);
            const positioning = abs <= 0.25
                ? "far OTM — lower premium, wider safety margin"
                : abs <= 0.40
                ? "moderate OTM — balanced premium and assignment risk"
                : "near ATM — higher premium, elevated assignment risk";
            bullets.push(`Δ ${Number(delta).toFixed(2)} — ${positioning}`);
        }

        if (premium != null && premium > 0) {
            const collected = (premium * 100).toFixed(2);
            const typeLabel = option_type === "call" ? "covered call" : "cash-secured put";
            bullets.push(`Collects $${collected} in premium per contract selling the ${typeLabel}`);
        }

        if (dte != null) {
            const decay = dte <= 14
                ? "rapid theta decay — premium erodes quickly near expiry"
                : dte <= 30
                ? "moderate theta decay — steady premium erosion"
                : "slower decay — more time buffer before expiry";
            bullets.push(`${dte} DTE — ${decay}`);
        }

        return bullets;
    }

    if (strategy === "tactical_spread" || strategy === "regime_spread") {
        const bullets = [];
        const { opportunity_strategy, width, dte } = meta;
        const debit = Number(meta.debit || preview.price || 0);

        if (opportunity_strategy) {
            const isBullish = opportunity_strategy === "call_debit_spread";
            bullets.push(`${isBullish ? "Bullish" : "Bearish"} directional trade — profits if price moves ${isBullish ? "higher" : "lower"} before expiry`);
        }

        if (debit > 0 && width > 0) {
            const maxLoss   = (debit * 100).toFixed(2);
            const maxProfit = ((Number(width) - debit) * 100).toFixed(2);
            const rr        = (Number(width) / debit).toFixed(1);
            bullets.push(`Max loss $${maxLoss} · max gain $${maxProfit} · ${rr}x reward-to-risk`);
        }

        if (dte != null) {
            bullets.push(`${dte} DTE — the trade must resolve within this window`);
        }

        return bullets;
    }

    if (strategy === "earnings") {
        const bullets = [];
        const { opportunity_strategy, score, confidence, earnings_date, days_to_earnings, width } = meta;
        const debit = Number(meta.debit || preview.price || 0);

        if (earnings_date) {
            bullets.push(`Earnings expected ${fmtDate(earnings_date)}${days_to_earnings != null ? ` — ${daysLabel(days_to_earnings)}` : ""}`);
        }

        if (opportunity_strategy) {
            const isBullish = opportunity_strategy === "call_debit_spread";
            bullets.push(`${isBullish ? "Bullish" : "Bearish"} debit spread — positioned for a move ${isBullish ? "higher" : "lower"} after the event`);
        }

        if (debit > 0 && width > 0) {
            const maxLoss   = (debit * 100).toFixed(2);
            const maxProfit = ((Number(width) - debit) * 100).toFixed(2);
            bullets.push(`Defined risk into the event — max loss $${maxLoss}, max gain $${maxProfit}`);
        }

        if (confidence) {
            bullets.push(`${confidence} conviction on this setup`);
        }

        if (score != null) {
            bullets.push(`Opportunity score ${Number(score).toFixed(0)} — based on price action, IV, and spread quality`);
        }

        return bullets;
    }

    return [];
}

export function EarningsWindow({ data }) {
    const event = data?.earnings;
    if (!event) return null;
    return (
        <div className="wcp-earnings-window">
            <div className="wcp-earnings-window-left">
                <span className="wcp-earnings-kicker">
                    Next earnings
                    {data.synthetic ? <span className="wcp-earnings-user-badge">user-entered</span> : null}
                </span>
                <span className="wcp-earnings-date-val">{fmtDate(event.earnings_date)}</span>
                <span className="wcp-earnings-tag">{daysLabel(event.days_to_earnings)}</span>
            </div>
            <div className="wcp-earnings-meta">
                {event.iv_rank != null ? <span>IV Rank <strong>{Number(event.iv_rank).toFixed(0)}</strong></span> : null}
                {event.price > 0 ? <span>Price <strong>${Number(event.price).toFixed(2)}</strong></span> : null}
            </div>
        </div>
    );
}

function buildEarningsPreview(symbol, opportunity, earningsData) {
    if (!opportunity) {
        return {
            action: "NO_ACTION",
            should_trade: false,
            reason: earningsData?.synthetic
                ? "The earnings date is saved, but there is no live spread setup yet."
                : `No earnings spread setup is available for ${symbol} right now.`,
            meta: {
                strategy: "earnings",
                earnings_date: earningsData?.earnings?.earnings_date,
                days_to_earnings: earningsData?.earnings?.days_to_earnings,
            },
        };
    }

    return {
        action: "OPEN_DEBIT_SPREAD",
        should_trade: true,
        reason: `Earnings setup found for ${symbol}.`,
        price: Number(opportunity.debit || 0),
        meta: {
            strategy: "earnings",
            opportunity_strategy: opportunity.strategy,
            long_strike: opportunity.long_strike,
            short_strike: opportunity.short_strike,
            width: opportunity.width,
            debit: opportunity.debit,
            dte: opportunity.spread_dte ?? opportunity.days_to_earnings,
            score: opportunity.score,
            confidence: opportunity.confidence,
            earnings_date: opportunity.earnings_date || earningsData?.earnings?.earnings_date,
            days_to_earnings: opportunity.days_to_earnings ?? earningsData?.earnings?.days_to_earnings,
        },
    };
}

// ------------------------------------------------------------------ //
// Component                                                            //
// ------------------------------------------------------------------ //

const WheelConfigPanel = ({ symbol, onBack, portfolioValue, buyingPower, strategy, accountSource = "trader", onStrategyChange }) => {
    const init = PRESETS.balanced;

    const [preset,        setPreset]        = useState("balanced");
    const [automation,    setAutomation]    = useState(null);
    const [showAutoConfirm, setShowAutoConfirm] = useState(false);
    const [maxCapital,    setMaxCapital]    = useState(500);
    const [takeProfitPct, setTakeProfitPct] = useState(init.take_profit);
    const [rollThreshold, setRollThreshold] = useState(init.roll_threshold);
    const [deltaMin,      setDeltaMin]      = useState(init.put_delta_min);
    const [deltaMax,      setDeltaMax]      = useState(init.put_delta_max);
    const [dteMin,        setDteMin]        = useState(init.min_dte);
    const [dteMax,        setDteMax]        = useState(init.max_dte);
    const [advancedOpen,  setAdvancedOpen]  = useState(false);
    const [trendStrengthThreshold, setTrendStrengthThreshold] = useState(0.03);
    const [tierSizing, setTierSizing] = useState(1.0);
    const [longHorizonDays, setLongHorizonDays] = useState(180);
    const [entryFilter, setEntryFilter] = useState("trend + iv");
    const [earningsData, setEarningsData] = useState(null);
    const [earningsLoading, setEarningsLoading] = useState(false);
    const [earningsError, setEarningsError] = useState(null);
    const [earningsDirection, setEarningsDirection] = useState(null);
    const [manualDate, setManualDate] = useState(null);
    const [showStratPicker, setShowStratPicker] = useState(false);
    const smallAccountMode = isSmallAccountMode(buyingPower);
    const strategyAllowed = isStrategyAllowedForBuyingPower(strategy, buyingPower);

    const [preview,          setPreview]          = useState(null);
    const [previewError,     setPreviewError]     = useState(null);
    const [executionResult,  setExecutionResult]  = useState(null);
    const [loadingPreview,   setLoadingPreview]   = useState(false);
    const [loadingExecution, setLoadingExecution] = useState(false);

    const [adaptedPreset,     setAdaptedPreset]     = useState(null);
    const [loadingAdaptation, setLoadingAdaptation] = useState(false);

    const [showSim,  setShowSim]  = useState(false);
    const [simLaunch, setSimLaunch] = useState(null);

    const fetchAdaptedPreset = useCallback(async (presetName) => {
        setLoadingAdaptation(true);
        try {
            const { data } = await axios.get(buildApiUrl("/active-preset"), {
                params: {
                    preset:       presetName,
                    account_size: portfolioValue || 0,
                },
                withCredentials: true,
            });
            setAdaptedPreset(data);
            // Apply adaptive values to the config sliders (only if user hasn't
            // manually opened advanced settings to override them)
            if (!advancedOpen) {
                setDeltaMin(data.delta_min);
                setDeltaMax(data.delta_max);
                setDteMin(data.dte_min);
                setDteMax(data.dte_max);
            }
        } catch {
            setAdaptedPreset(null);
        } finally {
            setLoadingAdaptation(false);
        }
    }, [portfolioValue, advancedOpen]);

    // Fetch adaptive preset on mount (intentionally only runs once)
    useEffect(() => {
        fetchAdaptedPreset("balanced");
    }, []); // eslint-disable-line

    const fetchEarnings = useCallback(async () => {
        if (!symbol || strategy !== "earnings" || !strategyAllowed) return;
        setEarningsLoading(true);
        setEarningsError(null);
        setEarningsData(null);
        setEarningsDirection(null);
        setManualDate(null);
        try {
            const data = await fetchEarningsOpportunity(symbol, portfolioValue, accountSource);
            if (data.found) {
                setEarningsData(data);
                if (data.best_opportunity) {
                    setEarningsDirection(data.best_opportunity.strategy);
                }
            } else {
                const stored = loadManualDates()[symbol];
                if (stored && allowsManualEarningsDate(data)) {
                    setManualDate(stored);
                    setEarningsData(buildSyntheticRecord(symbol, stored));
                } else {
                    setEarningsData(data);
                }
            }
        } catch (err) {
            if (err?.response?.status === 404) {
                const stored = loadManualDates()[symbol];
                if (stored) {
                    setManualDate(stored);
                    setEarningsData(buildSyntheticRecord(symbol, stored));
                } else {
                    setEarningsData({ found: false, symbol });
                }
            } else {
                setEarningsError("Could not load earnings data.");
            }
        } finally {
            setEarningsLoading(false);
        }
    }, [accountSource, portfolioValue, symbol, strategy, strategyAllowed]);

    useEffect(() => {
        if (strategy === "earnings") {
            fetchEarnings();
        } else {
            setEarningsData(null);
            setEarningsError(null);
            setEarningsDirection(null);
            setManualDate(null);
            setShowStratPicker(false);
        }
    }, [strategy, symbol, fetchEarnings]);

    const applyManualDate = (dateStr) => {
        if (!dateStr) return;
        saveManualDate(symbol, dateStr);
        setManualDate(dateStr);
        setEarningsData(buildSyntheticRecord(symbol, dateStr));
        setEarningsDirection(null);
        setPreview(null);
        setPreviewError(null);
    };

    const handlePresetChange = (p) => {
        const cfg = PRESETS[p];
        setPreset(p);
        setTakeProfitPct(cfg.take_profit);
        setRollThreshold(cfg.roll_threshold);
        // Apply base preset values immediately; adaptive values follow async
        setDeltaMin(cfg.put_delta_min);
        setDeltaMax(cfg.put_delta_max);
        setDteMin(cfg.min_dte);
        setDteMax(cfg.max_dte);
        setPreview(null);
        setPreviewError(null);
        setExecutionResult(null);
        setAdaptedPreset(null);
        fetchAdaptedPreset(p);
    };

    const buildPayload = (dryRun) => {
        return ({
        symbol,
        strategy,
        account_source: accountSource,
        preset,
        automation,
        automation_mode: automation ? "auto" : "manual",
        portfolio_context: {
            portfolio_value: portfolioValue || 0,
            buying_power: buyingPower ?? 0,
            account_source: accountSource,
            strategy,
            source: "configure_trade",
        },
        config: {
            max_capital_per_trade: maxCapital,
            take_profit_pct:       takeProfitPct,
            roll_threshold:        rollThreshold,
            put_delta_min:         deltaMin,
            put_delta_max:         deltaMax,
            call_delta_min:        Math.abs(deltaMax),
            call_delta_max:        Math.abs(deltaMin),
            min_dte:               dteMin,
            max_dte:               dteMax,
            trend_strength_threshold: trendStrengthThreshold,
            tier_sizing:           tierSizing,
            long_horizon_days:     longHorizonDays,
            entry_filter:          entryFilter,
        },
        dry_run: dryRun,
    });
    };

    const selectedOpp = earningsData?.opportunities?.find(
        (opportunity) => opportunity.strategy === earningsDirection
    ) ?? earningsData?.best_opportunity ?? null;

    const handlePreview = async () => {
        if (automation === null) {
            toast.error("Select Manual or Auto mode before previewing.");
            return;
        }
        if (!strategyAllowed) {
            setPreview(null);
            setPreviewError(SMALL_ACCOUNT_REJECTION_MESSAGE);
            return;
        }
        setPreview(null);
        setPreviewError(null);
        setExecutionResult(null);
        setLoadingPreview(true);
        try {
            if (strategy === "earnings") {
                setPreview(buildEarningsPreview(symbol, selectedOpp, earningsData));
                return;
            }
            const { data } = await axios.post(
                buildApiUrl("/execute-strategy"),
                buildPayload(true),
                { withCredentials: true }
            );
            setPreview(data.decision);
        } catch (err) {
            const raw = err.response?.data?.details || err.response?.data?.error || err.message || "";
            setPreviewError(parseApiError(raw));
        } finally {
            setLoadingPreview(false);
        }
    };

    const doExecute = async () => {
        setLoadingExecution(true);
        try {
            const { data } = await axios.post(
                buildApiUrl("/execute-strategy"),
                buildPayload(false),
                { withCredentials: true, timeout: EXECUTION_TIMEOUT_MS }
            );
            const execution = data.execution;
            setExecutionResult(execution);
            const toastFeedback = buildExecutionToastMessage(execution, data);
            fireTradeToast(toastFeedback.message, toastFeedback.type);
        } catch (err) {
            const reason = formatExecutionError(err, "Execution failed.");
            setExecutionResult({ status: "error", error: reason });
            fireTradeToast(reason, "error");
        } finally {
            setLoadingExecution(false);
        }
    };

    const handleExecute = () => {
        if (automation === null) {
            toast.error("Select Manual or Auto mode before executing.");
            return;
        }
        if (!strategyAllowed) {
            toast.error(SMALL_ACCOUNT_REJECTION_MESSAGE);
            return;
        }
        if (strategy === "earnings" && earningsData?.synthetic) {
            toast.info("The saved earnings date helps plan the trade, but live execution still needs a real earnings event from the market feed.");
            return;
        }
        if (automation === true) {
            setShowAutoConfirm(true);
            return;
        }
        doExecute();
    };

    const strategyLabel    = formatStrategyLabel(strategy);
    const actionLabel      = ACTION_LABELS[preview?.action] || preview?.action;
    const contractSummary  = formatContractSummary(preview?.meta);
    const reason           = preview?.reason || preview?.meta?.reason;
    const tradeExplanation = buildTradeExplanation(preview, symbol);
    const whyBullets       = buildWhyBullets(preview, strategy, maxCapital);

    // Derived behavior outputs
    const closePriceLabel = preview?.price != null
        ? `Close at $${(preview.price * (1 - takeProfitPct)).toFixed(2)}`
        : `${Math.round(takeProfitPct * 100)}% of premium collected`;

    return (
        <motion.div
            className="wheel-config-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.button
                className="wcp-back-btn"
                variants={sectionVariants}
                onClick={onBack}
            >
                ← Back
            </motion.button>
            <motion.div className="wcp-symbol" variants={sectionVariants}>
                {symbol} — {strategyLabel}
            </motion.div>

            <motion.div className="wcp-section" variants={sectionVariants}>
                <div className="wcp-label">Strategy</div>
                {smallAccountMode && <div className="wcp-small-account-badge">Small Account Mode</div>}
                <div className="wcp-toggle-row">
                    {STRATEGY_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            title={!isStrategyAllowedForBuyingPower(option.id, buyingPower) ? SMALL_ACCOUNT_TOOLTIP : ""}
                            className={`wcp-toggle ${strategy === option.id ? "active" : ""} ${!isStrategyAllowedForBuyingPower(option.id, buyingPower) ? "disabled" : ""}`}
                            onClick={() => {
                                if (!isStrategyAllowedForBuyingPower(option.id, buyingPower)) {
                                    setPreviewError(SMALL_ACCOUNT_REJECTION_MESSAGE);
                                    return;
                                }
                                onStrategyChange?.(option.id);
                            }}
                            type="button"
                            disabled={!isStrategyAllowedForBuyingPower(option.id, buyingPower)}
                        >
                            {option.label.replace(" Strategy", "")}
                        </button>
                    ))}
                </div>
                {!strategyAllowed && <div className="wcp-strategy-disabled-note">{SMALL_ACCOUNT_REJECTION_MESSAGE}</div>}
            </motion.div>

            {/* ---- Presets ---- */}
            {strategy === "wheel" && (
            <motion.div className="wcp-section" variants={sectionVariants}>
                <div className="wcp-label">Preset</div>
                <div className="wcp-presets">
                    {Object.entries(PRESETS).map(([key, val]) => (
                        <motion.div
                            key={key}
                            className={`wcp-preset ${preset === key ? "active" : ""}`}
                            onClick={() => handlePresetChange(key)}
                            animate={{ scale: preset === key ? 1.03 : 1 }}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                        >
                            <div className="wcp-preset-name">
                                {val.label}
                                {preset === key && adaptedPreset?.is_adapted && (
                                    <span className="wcp-adaptive-badge">Adaptive</span>
                                )}
                            </div>
                            <div className="wcp-preset-desc">{val.description}</div>
                        </motion.div>
                    ))}
                </div>

                {/* Adaptive preset info strip */}
                <AnimatePresence mode="wait">
                    {loadingAdaptation ? (
                        <motion.div
                            key="adapting"
                            className="wcp-adaptive-strip wcp-adaptive-strip--loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            <div className="wcp-skeleton" style={{ width: "60%", height: 9, borderRadius: 4 }} />
                            <div className="wcp-skeleton" style={{ width: "80%", height: 8, borderRadius: 4, marginTop: 4 }} />
                        </motion.div>
                    ) : adaptedPreset ? (
                        <motion.div
                            key={`adapted-${preset}`}
                            className="wcp-adaptive-strip"
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                            <div className="wcp-adaptive-params">
                                Delta {adaptedPreset.delta_min} to {adaptedPreset.delta_max}
                                <span className="wcp-adaptive-sep">·</span>
                                DTE {adaptedPreset.dte_min}–{adaptedPreset.dte_max}
                            </div>
                            <div className="wcp-adaptive-reason">{adaptedPreset.explanation}</div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="summary"
                            className="wcp-preset-summary"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            {PRESETS[preset].summary}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
            )}

            {/* ---- Automation ---- */}
            <motion.div className="wcp-section" variants={sectionVariants}>
                <div className="wcp-label">
                    Automation
                    {automation === null && <span className="wcp-required-badge">Required</span>}
                </div>
                <div className="wcp-toggle-row">
                    <button
                        className={`wcp-toggle ${automation === false ? "active" : ""}`}
                        onClick={() => setAutomation(false)}
                        type="button"
                    >
                        Manual
                    </button>
                    <button
                        className={`wcp-toggle ${automation === true ? "active" : ""}`}
                        onClick={() => setAutomation(true)}
                        type="button"
                    >
                        Auto
                    </button>
                </div>
                <div className="wcp-hint">
                    {automation === null && "Choose how trades execute — required before previewing or executing."}
                    {automation === true && "Trades execute automatically — no per-trade confirmation required."}
                    {automation === false && "Manual approval required — you review each trade before it executes."}
                </div>
            </motion.div>

            {/* ---- Risk ---- */}
            <motion.div className="wcp-section" variants={sectionVariants}>
                <div className="wcp-label">Risk</div>
                <div className="wcp-row">
                    <span>Max capital per trade</span>
                    <input
                        type="number"
                        className="wcp-input"
                        value={maxCapital}
                        onChange={(e) => setMaxCapital(Number(e.target.value))}
                        min={100}
                        step={100}
                    />
                </div>
            </motion.div>

            {/* ---- Behavior ---- */}
            <motion.div className="wcp-section" variants={sectionVariants}>
                <div className="wcp-label">Behavior</div>
                {strategy === "wheel" && (
                <>
                <div className="wcp-row">
                    <div className="wcp-row-left">
                        <span>Take profit</span>
                        <span className="wcp-derived">{closePriceLabel}</span>
                    </div>
                    <input
                        type="number"
                        className="wcp-input"
                        value={takeProfitPct}
                        onChange={(e) => setTakeProfitPct(Number(e.target.value))}
                        min={0.1} max={1.0} step={0.05}
                    />
                </div>
                <div className="wcp-row">
                    <div className="wcp-row-left">
                        <span>Roll threshold</span>
                        <span className="wcp-derived">Roll at {rollThreshold}× initial delta</span>
                    </div>
                    <input
                        type="number"
                        className="wcp-input"
                        value={rollThreshold}
                        onChange={(e) => setRollThreshold(Number(e.target.value))}
                        min={1.0} step={0.5}
                    />
                </div>
                </>
                )}
                {strategy === "tactical_spread" && (
                <>
                <div className="wcp-row">
                    <div className="wcp-row-left">
                        <span>DTE range</span>
                        <span className="wcp-derived">{dteMin}–{dteMax} days</span>
                    </div>
                    <input
                        type="number"
                        className="wcp-input"
                        value={dteMin}
                        onChange={(e) => setDteMin(Number(e.target.value))}
                        min={1} max={90} step={1}
                    />
                </div>
                <div className="wcp-row">
                    <span>Trend strength threshold</span>
                    <input
                        type="number"
                        className="wcp-input"
                        value={trendStrengthThreshold}
                        onChange={(e) => setTrendStrengthThreshold(Number(e.target.value))}
                        min={0} max={1} step={0.01}
                    />
                </div>
                <div className="wcp-row">
                    <span>Tier sizing</span>
                    <input
                        type="number"
                        className="wcp-input"
                        value={tierSizing}
                        onChange={(e) => setTierSizing(Number(e.target.value))}
                        min={0.1} max={3} step={0.1}
                    />
                </div>
                </>
                )}
                {strategy === "regime_spread" && (
                <>
                <div className="wcp-row">
                    <span>Long horizon setting</span>
                    <input
                        type="number"
                        className="wcp-input"
                        value={longHorizonDays}
                        onChange={(e) => setLongHorizonDays(Number(e.target.value))}
                        min={30} max={365} step={5}
                    />
                </div>
                <div className="wcp-row">
                    <div className="wcp-row-left">
                        <span>Entry filters</span>
                        <span className="wcp-derived">{entryFilter}</span>
                    </div>
                    <input
                        type="text"
                        className="wcp-input"
                        value={entryFilter}
                        onChange={(e) => setEntryFilter(e.target.value)}
                    />
                </div>
                </>
                )}
                {strategy === "earnings" && (
                <div className="wcp-earnings-section">
                    <div className="wcp-hint">
                        Earnings ideas pull the next known event for this symbol. If no event is available, you can save an expected date for planning.
                    </div>

                    {earningsLoading ? (
                        <div className="wcp-earnings-loading">Fetching earnings for {symbol}…</div>
                    ) : null}

                    {!earningsLoading && earningsError ? (
                        <div className="wcp-preview-card wcp-preview-card--error">
                            <div className="wcp-preview-card-title">Unable to load earnings</div>
                            <div className="wcp-preview-card-reason">{earningsError}</div>
                        </div>
                    ) : null}

                    {!earningsLoading && earningsData && !earningsData.found ? (
                        <EarningsEmptyState
                            earningsData={earningsData}
                            symbol={symbol}
                            manualDate={manualDate}
                            onManualDateChange={applyManualDate}
                        />
                    ) : null}

                    {!earningsLoading && earningsData?.found ? (
                        <div className="wcp-earnings-panel">
                            <EarningsWindow data={earningsData} />

                            {earningsData.synthetic ? (
                                <div className="wcp-manual-date-section">
                                    <label className="wcp-label">Change date</label>
                                    <input
                                        type="date"
                                        className="wcp-date-input"
                                        value={manualDate || ""}
                                        min={new Date().toISOString().split("T")[0]}
                                        onChange={(event) => applyManualDate(event.target.value)}
                                    />
                                </div>
                            ) : null}

                            {earningsData.opportunities?.length > 0 ? (
                                <>
                                    <div className="wcp-strat-selected-row">
                                        <div className="wcp-strat-selected">
                                            <span className="wcp-strat-selected-dir">
                                                {selectedOpp?.strategy === "call_debit_spread" ? "↑ Bullish" : "↓ Bearish"}
                                            </span>
                                            <span className="wcp-strat-selected-name">
                                                {selectedOpp?.strategy === "call_debit_spread" ? "Call debit spread" : "Put debit spread"}
                                            </span>
                                            {selectedOpp ? (
                                                <span className={`wcp-opp-conf wcp-opp-conf--${String(selectedOpp.confidence || "").toLowerCase()}`}>
                                                    {selectedOpp.confidence}
                                                </span>
                                            ) : null}
                                            {selectedOpp?.score != null ? (
                                                <span className="wcp-strat-score">{selectedOpp.score}</span>
                                            ) : null}
                                        </div>
                                        <button
                                            className="wcp-strat-pencil"
                                            onClick={() => setShowStratPicker((open) => !open)}
                                            type="button"
                                            title="Change earnings setup"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M8.5 1.5L10.5 3.5L4 10H2V8L8.5 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                    <AnimatePresence>
                                        {showStratPicker ? (
                                            <motion.div
                                                className="wcp-strat-list"
                                                initial={{ opacity: 0, y: -4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -4 }}
                                                transition={{ duration: 0.14 }}
                                            >
                                                {earningsData.opportunities.map((opp) => {
                                                    const isCall = opp.strategy === "call_debit_spread";
                                                    const confidenceClass = String(opp.confidence || "").toLowerCase();
                                                    return (
                                                        <button
                                                            key={opp.strategy}
                                                            className={`wcp-strat-item ${earningsDirection === opp.strategy ? "wcp-strat-item--active" : ""}`}
                                                            onClick={() => {
                                                                setEarningsDirection(opp.strategy);
                                                                setShowStratPicker(false);
                                                            }}
                                                            type="button"
                                                        >
                                                            <div className="wcp-strat-item-left">
                                                                <span className="wcp-strat-item-dir">{isCall ? "↑ Bullish" : "↓ Bearish"}</span>
                                                                <span className="wcp-strat-item-name">{isCall ? "Call debit spread" : "Put debit spread"}</span>
                                                            </div>
                                                            <div className="wcp-strat-item-right">
                                                                <span className={`wcp-opp-conf wcp-opp-conf--${confidenceClass}`}>{opp.confidence}</span>
                                                                <span className="wcp-strat-score">{opp.score}</span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </motion.div>
                                        ) : null}
                                    </AnimatePresence>
                                </>
                            ) : (
                                <div className="wcp-hint">
                                    We found the event, but no earnings spread candidates are available yet.
                                </div>
                            )}

                            {earningsData.synthetic ? (
                                <div className="wcp-hint">
                                    This saved date is for planning only. Live execution still needs a real market earnings event.
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
                )}
            </motion.div>

            {/* ---- Advanced settings ---- */}
            {strategy === "wheel" && (
            <motion.div className="wcp-advanced" variants={sectionVariants}>
                <button className="wcp-advanced-toggle" onClick={() => setAdvancedOpen(o => !o)}>
                    <span>⚙ Advanced Settings</span>
                    <motion.span
                        className="wcp-advanced-arrow"
                        animate={{ rotate: advancedOpen ? 180 : 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        style={{ display: "inline-block" }}
                    >
                        ▼
                    </motion.span>
                </button>
                <AnimatePresence>
                    {advancedOpen && (
                        <motion.div
                            className="wcp-advanced-body"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                        >
                            <div className="wcp-advanced-hint">
                                Overrides preset defaults. Changes take effect on next preview.
                            </div>
                            <div className="wcp-label" style={{ marginTop: 8 }}>Put delta range</div>
                            <div className="wcp-row">
                                <span>Min</span>
                                <input
                                    type="number"
                                    className="wcp-input"
                                    value={deltaMin}
                                    onChange={(e) => setDeltaMin(Number(e.target.value))}
                                    min={-0.99} max={0} step={0.05}
                                />
                            </div>
                            <div className="wcp-row">
                                <span>Max</span>
                                <input
                                    type="number"
                                    className="wcp-input"
                                    value={deltaMax}
                                    onChange={(e) => setDeltaMax(Number(e.target.value))}
                                    min={-0.99} max={0} step={0.05}
                                />
                            </div>
                            <div className="wcp-label" style={{ marginTop: 8 }}>Days to expiration</div>
                            <div className="wcp-row">
                                <span>Min DTE</span>
                                <input
                                    type="number"
                                    className="wcp-input"
                                    value={dteMin}
                                    onChange={(e) => setDteMin(Number(e.target.value))}
                                    min={1} max={90} step={1}
                                />
                            </div>
                            <div className="wcp-row">
                                <span>Max DTE</span>
                                <input
                                    type="number"
                                    className="wcp-input"
                                    value={dteMax}
                                    onChange={(e) => setDteMax(Number(e.target.value))}
                                    min={1} max={180} step={1}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
            )}

            {/* ---- Preview button ---- */}
            <motion.button
                className="wcp-start-btn"
                variants={sectionVariants}
                onClick={handlePreview}
                disabled={loadingPreview || loadingExecution}
                whileHover={{ scale: loadingPreview ? 1 : 1.02 }}
                whileTap={{ scale: loadingPreview ? 1 : 0.97 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
            >
                {loadingPreview ? `Analyzing ${symbol}...` : "Preview Trade →"}
            </motion.button>

            {/* ---- Preview card ---- */}
            <AnimatePresence mode="wait">

                {loadingPreview && (
                    <motion.div
                        key="loading"
                        className="wcp-preview-card"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <PreviewSkeleton />
                    </motion.div>
                )}

                {previewError && !loadingPreview && (
                    <motion.div
                        key="error"
                        className="wcp-preview-card wcp-preview-card--error"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <div className="wcp-preview-card-title">Unable to load preview</div>
                        <div className="wcp-preview-card-reason">{previewError}</div>
                    </motion.div>
                )}

                {preview && !previewError && !loadingPreview && (
                    <motion.div
                        key="preview"
                        className="wcp-preview-card"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover="hover"
                        whileTap="tap"
                    >
                        {preview.should_trade ? (
                            <>
                                {/* Title */}
                                <div className="wcp-preview-card-title">
                                    {symbol} — {actionLabel}
                                </div>

                                {/* Premium */}
                                {preview.price != null && (
                                    <div className="wcp-preview-card-premium">
                                        ${Number(preview.price).toFixed(2)}
                                        <span className="wcp-preview-card-premium-label">premium / contract</span>
                                    </div>
                                )}

                                {/* Contract line with delta */}
                                {contractSummary && (
                                    <div className="wcp-preview-card-contract">{contractSummary}</div>
                                )}

                                {/* What this trade does */}
                                {tradeExplanation && (
                                    <div className="wcp-trade-explanation">{tradeExplanation}</div>
                                )}

                                {/* Why this trade? */}
                                {whyBullets.length > 0 && (
                                    <div className="wcp-why-section">
                                        <div className="wcp-why-title">Why this trade?</div>
                                        <ul className="wcp-why-list">
                                            {whyBullets.map((b, i) => <li key={i}>{b}</li>)}
                                        </ul>
                                    </div>
                                )}

                                {/* Queued warning */}
                                {executionResult?.status === "queued" && (
                                    <div className="wcp-preview-card-warning">
                                        Market is closed — order queued for 9:30 AM
                                    </div>
                                )}

                                {/* CTA or result */}
                                {!executionResult ? (
                                    <motion.button
                                        className="wcp-execute-btn"
                                        onClick={handleExecute}
                                        disabled={loadingExecution}
                                        whileHover={{ scale: loadingExecution ? 1 : 1.02 }}
                                        whileTap={{ scale: loadingExecution ? 1 : 0.97 }}
                                        transition={{ duration: 0.15, ease: "easeInOut" }}
                                    >
                                        {loadingExecution ? "Submitting..." : "Execute Trade"}
                                    </motion.button>
                                ) : (
                                    <motion.div
                                        className={`wcp-result wcp-result--${executionResult.status}`}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, ease: "easeOut" }}
                                    >
                                        {executionResult.status === "submitted" && <>✓ Order submitted · {executionResult.order_id}</>}
                                        {executionResult.status === "queued"    && <>Order queued for market open (9:30 AM)</>}
                                        {executionResult.status === "dry_run"   && <>Dry run complete — no order placed</>}
                                        {executionResult.status === "skipped"   && <>{executionResult.message || "Skipped"}</>}
                                        {executionResult.status === "error"     && <>✕ {executionResult.error || "Execution failed"}</>}
                                    </motion.div>
                                )}

                                {/* Simulate Trade */}
                                <motion.button
                                    className="wcp-simulate-btn"
                                    onClick={() => {
                                        const launch = buildSimLaunch(strategy, symbol, portfolioValue, buyingPower, preset);
                                        setSimLaunch(launch);
                                        setShowSim(true);
                                    }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    transition={{ duration: 0.15, ease: "easeInOut" }}
                                >
                                    Simulate Trade
                                </motion.button>
                            </>
                        ) : (
                            <>
                                <div className="wcp-preview-card-title">No trade available</div>
                                {reason && <div className="wcp-preview-card-reason">{reason}</div>}
                            </>
                        )}
                    </motion.div>
                )}

            </AnimatePresence>

            {/* ---- Simulation panel ---- */}
            <AnimatePresence>
                {showSim && (
                    <motion.div
                        key="sim-panel"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                        <SimulatePanel
                            defaultBuyingPower={buyingPower}
                            symbol={symbol}
                            launchRequest={simLaunch}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ---- Auto mode confirmation modal ---- */}
            <AnimatePresence>
                {showAutoConfirm && (
                    <motion.div
                        className="wcp-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        onClick={() => setShowAutoConfirm(false)}
                    >
                        <motion.div
                            className="wcp-modal"
                            initial={{ opacity: 0, scale: 0.96, y: 8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: 4 }}
                            transition={{ duration: 0.18, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="wcp-modal-title">Execute in Auto Mode</div>
                            <div className="wcp-modal-body">
                                Trades will execute automatically without manual approval for each order.
                                Confirm you want to proceed with live execution.
                            </div>
                            <div className="wcp-modal-actions">
                                <button
                                    type="button"
                                    className="wcp-modal-cancel"
                                    onClick={() => setShowAutoConfirm(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="wcp-modal-confirm"
                                    onClick={() => { setShowAutoConfirm(false); doExecute(); }}
                                    disabled={loadingExecution}
                                >
                                    {loadingExecution ? "Submitting..." : "Confirm & Execute"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default WheelConfigPanel;
